<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Row 1: Search + Filters -->
            <div class="col-12 d-flex flex-wrap align-items-center mb-2">
               <div class="me-auto" style="width: 220px;">
                  <input v-model="search" type="text" class="form-control form-control-sm"
                     placeholder="&#xF002; Search blocked websites..."
                     style="border-radius:8px; height: 36px; font-size: 13px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                     @keyup="onSearch()" />
               </div>

               <div class="d-flex align-items-center me-2">
                  <span class="p-1 filterTitle">Reviewed:</span>
                  <select v-model="filterReviewBy" class="form-select shadow-none filterOptions bordered-select" style="font-size: 13px;">
                     <option v-for="f in filterReviewed" :value="f.value" :key="f.value">{{ f.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-2">
                  <span class="p-1 filterTitle">Allowed:</span>
                  <select v-model="filterAllowedBy" class="form-select shadow-none filterOptions bordered-select" style="font-size: 13px;">
                     <option v-for="f in filterAllowed" :value="f.value" :key="f.value">{{ f.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-2">
                  <span class="p-1 filterTitle">Account:</span>
                  <select v-model="filterAccountBy" class="form-select shadow-none filterOptions bordered-select" style="font-size: 13px;">
                     <option v-for="f in filterAccount" :value="f.value" :key="f.value" :title="f.text">{{ truncateText(f.text, 15) }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-2">
                  <span class="p-1 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select" style="font-size: 13px;">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-2">
                  <span class="p-1 filterTitle">Rows:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto; font-size: 13px;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col">Account</th>
                     <th scope="col">URL</th>
                     <th scope="col">Reason</th>
                     <th scope="col" style="width: 100px;">Reviewed</th>
                     <th scope="col" style="width: 100px;">Allowed</th>
                     <th scope="col" style="width: 160px;">Date</th>
                     <th scope="col" style="width: 130px;">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0">
                  <tr>
                     <td colspan="7">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(bw, index) in blockedWebsites" :key="index">
                     <td :title="bw.account_name">{{ truncateText(bw.account_name, 20) || '—' }}</td>
                     <td :title="bw.url" style="font-family: monospace; font-size: 12px;">
                        {{ truncateText(trimQuestionMark(bw.url), 45) }}
                        <button type="button" class="ms-action-btn ms-action-btn--edit" title="Copy URL"
                           @click="copyURL(bw)">
                           <i class="fa-solid fa-copy"></i>
                        </button>
                        <input type="hidden" :id="'id_blockedwebsite_' + bw.id" :value="bw.url">
                     </td>
                     <td>
                        <span class="code-badge me-1">{{ bw.unsafe_code }}</span>
                        <span class="reason-text">{{ getUnsafeDescription(bw.unsafe_code) }}</span>
                     </td>
                     <td>
                        <span class="status-pill" :class="bw.is_reviewed == 0 ? 'status-pending' : 'status-done'">
                           <i :class="bw.is_reviewed == 0 ? 'fa-solid fa-clock' : 'fa-solid fa-circle-check'" class="me-1"></i>
                           {{ bw.is_reviewed == 0 ? 'No' : 'Yes' }}
                        </span>
                     </td>
                     <td>
                        <span class="status-pill" :class="bw.is_allowed == 0 ? 'status-blocked' : 'status-allowed'">
                           <i :class="bw.is_allowed == 0 ? 'fa-solid fa-ban' : 'fa-solid fa-circle-check'" class="me-1"></i>
                           {{ bw.is_allowed == 0 ? 'No' : 'Yes' }}
                        </span>
                     </td>
                     <td class="date-text">{{ formatDate(bw.date_created) }}</td>
                     <td>
                        <div class="d-flex gap-2">
                           <button v-if="bw.is_allowed == 0 || bw.is_reviewed == 0"
                              type="button" class="ms-action-btn ms-action-btn--success"
                              title="Mark as Safe" @click="markStatus(bw, 1)">
                              <i class="fa-solid fa-check"></i>
                           </button>
                           <button v-if="bw.is_allowed == 1 || bw.is_reviewed == 0"
                              type="button" class="ms-action-btn ms-action-btn--delete"
                              title="Mark as Unsafe" @click="markStatus(bw, 0)">
                              <i class="fa-solid fa-xmark"></i>
                           </button>
                           <button type="button" class="ms-action-btn ms-action-btn--delete"
                              title="Delete" @click="deleteBlockedWebsite(bw.id)">
                              <i class="fa-solid fa-trash"></i>
                           </button>
                        </div>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>
</template>

<script>
import moment from 'moment'
import { debounce, extractErrorMessage } from '@/plugins/helper.js'

export default {
   data() {
      return {
         search: "",
         blockedWebsites: [],
         debounceFn: null,
         paginationData: {},
         filterReviewed: [
            { text: "Reviewed", value: 1 },
            { text: "Not Reviewed", value: 0 },
            { text: "Both", value: 2 },
         ],
         filterReviewBy: 2,
         filterAllowed: [
            { text: "Allowed", value: 1 },
            { text: "Not Allowed", value: 0 },
            { text: "Both", value: 2 },
         ],
         filterAllowedBy: 2,
         filterAccount: [
            { text: "All Accounts", value: "all_accounts" },
         ],
         filterAccountBy: "all_accounts",
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Date: Newest", value: "date_created__desc" },
            { text: "Date: Oldest", value: "date_created__asc" },
            { text: "URL: A-Z", value: "url__asc" },
            { text: "URL: Z-A", value: "url__desc" },
            { text: "Account: A-Z", value: "account_name__asc" },
            { text: "Account: Z-A", value: "account_name__desc" },
         ],
         sortBy: "date_created__desc",
         sorting: { field: "date_created", order: "desc" },
      }
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      filterByReview() { return this.filterReviewBy },
      filterByAllowed() { return this.filterAllowedBy },
      filterByAccount() { return this.filterAccountBy },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() { this.getBlockedWebsites() },
      filterByReview() { this.getBlockedWebsites() },
      filterByAccount() { this.getBlockedWebsites() },
      filterByAllowed() { this.getBlockedWebsites() },
      rowsPerPage() { this.pagination.page = 1; this.getBlockedWebsites() },
      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getBlockedWebsites()
      },
   },

   created() {
      this.debounceFn = debounce(() => this.getBlockedWebsites(), 500)
      this.init()
   },

   methods: {
      init() {
         this.getBlockedWebsites()
         let orgs = this.$store.state.auth.organisations
         if (orgs && orgs.length > 0) {
            for (let x = 0; x < orgs.length; x++) {
               this.filterAccount.push({ text: orgs[x].name, value: orgs[x].orguuid })
            }
         }
      },

      formatDate(date) { return moment(date).format("DD-MMM-YYYY, hh:mm A") },

      truncateText(text, limit) {
         if (!text) return '';
         return text.length <= limit ? text : text.slice(0, limit) + '...';
      },

      trimQuestionMark(text) {
         if (!text) return '';
         let index = text.indexOf('?');
         return index > 0 ? text.substring(0, index) : text;
      },

      getUnsafeDescription(code) {
         const descriptions = {
            315: "Blocked from block list",
            346: "Block HTTP website is enabled",
            347: "Site age is less than 4 months",
            348: "Domain is unsafe on Google Safe Browsing",
            349: "Domain is unsafe on Webshrinker",
            350: "Unsafe domain status",
            351: "Block all file sharing websites",
            352: "URL contains an email address",
            353: "Domain is longer than 50 characters",
            354: "Block chat messaging websites",
            355: "Block Webshrinker category",
            356: "Website exists in unsafe websites table",
            357: "Block all websites except allowed",
            358: "Info TLD with hyphen (phishing)",
            359: "Unsafe site for 6 months",
            360: "Block OAuth site",
         };
         return descriptions[code] || `Code ${code}`;
      },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },
      onSearch() { this.pagination.page = 1; this.debounceFn() },

      copyURL(bw) {
         let el = document.querySelector(`#id_blockedwebsite_${bw.id}`)
         el.setAttribute('type', 'text')
         el.select()
         document.execCommand('copy')
         el.setAttribute('type', 'hidden')
         window.getSelection().removeAllRanges()
         this.$toast.success('URL copied to clipboard.')
      },

      async markStatus(website, is_safe) {
         try {
            const payload = { id: website.id, domain: website.url, is_safe: is_safe, unsafe_code: `${website.unsafe_code}` }
            const resp = await this.$axios.post("admin-controls/review-blocked-website", payload)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getBlockedWebsites()
            }
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to update review status."))
         }
      },

      async deleteBlockedWebsite(id) {
         try {
            const resp = await this.$axios.delete("admin-controls/remove-blocked-website/" + id)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getBlockedWebsites()
            }
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to remove blocked website."))
         }
      },

      async getBlockedWebsites() {
         let apiPath = "admin-controls/get-blocked-websites?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page + "&organisation=" + this.filterAccountBy

         if (this.sorting.order != "" && this.sorting.field != "") {
            apiPath += "&order_by=" + this.sorting.order
            apiPath += "&field=" + this.sorting.field
         }
         if (this.search !== "" || this.search !== null) {
            apiPath += "&url=" + this.search
         }
         apiPath += "&is_allowed=" + this.filterAllowedBy
         apiPath += "&is_reviewed=" + this.filterReviewBy

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.blocked_websites === "No Record Found")
                  this.blockedWebsites = []
               else {
                  this.blockedWebsites = response.data.blocked_websites
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               console.log(error)
               this.$toast.error(extractErrorMessage(error, "Failed to load blocked websites."))
            })
      },
   },
}
</script>

<style scoped>
.card { border: none; }

.date-text { font-size: 13px; color: #6c757d; white-space: nowrap; }

/* Status pills */
.status-pill {
   padding: 4px 10px; border-radius: 6px;
   font-size: 11px; font-weight: 600;
   display: inline-flex; align-items: center; white-space: nowrap;
}
.status-pending { background: #fff8e1; color: #e6a100; }
.status-done { background: #e6f9ee; color: #00873c; }
.status-blocked { background: #fde8ea; color: #c62828; }
.status-allowed { background: #e6f9ee; color: #00873c; }

/* Code badge */
.code-badge {
   font-family: monospace; font-size: 11px;
   background: #f1f3f5; color: #455a64;
   padding: 3px 8px; border-radius: 4px;
   font-weight: 600; cursor: help;
}
.reason-text { font-size: 12px; color: #6c757d; }

/* Copy button */
.btn-copy {
   width: 24px; height: 24px; padding: 0;
   border: none; background: none; color: #adb5bd;
   font-size: 11px; display: inline-flex;
   align-items: center; justify-content: center;
   cursor: pointer; transition: color 0.15s;
}
.btn-copy:hover { color: #00A0C1; }

/* Action buttons */
.btn-action {
   width: 30px; height: 30px; padding: 0;
   border-radius: 6px; display: inline-flex;
   align-items: center; justify-content: center;
   font-size: 12px; transition: all 0.15s ease;
}
.btn-action-safe { border: 1px solid #00873c; color: #00873c; background: #fff; }
.btn-action-safe:hover { background: #00873c; color: #fff; }
.btn-action-unsafe { border: 1px solid #c62828; color: #c62828; background: #fff; }
.btn-action-unsafe:hover { background: #c62828; color: #fff; }
.btn-action-delete { border: 1px solid #c62828; color: #c62828; background: #fff; }
.btn-action-delete:hover { background: #c62828; color: #fff; }
</style>